import {getSearchViewConfig, localStorageConfig} from "./localStorageConfig";
import decamelize from "decamelize";
import {clientLog} from "../clientLog";
import {useMemo} from "react";

/**
 * Custom hook to manage configuration settings for different views.
 */
export const useConfig = () => {

    // List of valid keys for view configurations
    const validViewConfigKeys = useMemo(() => ([
        'general', // Global config. settings stored under this key
        'mainSearch',
        'folderContentsSearch',
        'folderSearch',
        'folderListSearch',
        'projectSearch',
        'projectContentsSearch',
        'filters'
    ]), []);

    /**
     * Loads the configuration for a specific view key.
     *
     * @param {string} key - The key representing the view configuration.
     * @returns {object} The configuration object for the specified key.
     * @throws {Error} If the provided key is not valid.
     */
    const _loadViewConfig = useMemo(() => (key) => {
        if (!validViewConfigKeys.includes(key)) {
            throw new Error('invalid view key for config: ' + key);
        }
        return getSearchViewConfig(key);
    }, [validViewConfigKeys]);

    const _setDefaultViewConfig = (viewName) => {
        const defaultViewConfig = localStorageConfig[viewName];
        try {
            const savedConfig = JSON.parse(localStorage.getItem('dams')) || {};
            localStorage.setItem('dams', JSON.stringify({...savedConfig, [viewName]: defaultViewConfig}));
            clientLog('debug', `setDefaultViewConfig for view: ${viewName}`, 'useConfig');
        } catch (e) {
            clientLog('error',
                `error setting default config for view: ${viewName} ${e}`,
                'useConfig');
        }
    };

    const _parseValue = (viewName, key, value) => {
        if (key === 'sort') {
            return `${decamelize(value.column)} ${value.order}`;
        }
        clientLog('debug',
            () => `getConfig for view: ${viewName} key: ${key} value: ${JSON.stringify(value)}`,
            'useConfig');
        return value;
    };

    /**
     * Retrieves a specific configuration value for a given view and key.
     *
     * @param {string} viewName - The name of the view.
     * @param {string} key - The key of the configuration value to retrieve.
     * @returns {*} The configuration value.
     * @throws {Error} If the view name or key is invalid.
     */
    const getConfig = (viewName, key) => {
        const config = _loadViewConfig(viewName);
        if (!config) {
            clientLog('warn',
                `no config found for view: ${viewName}, returning default config.`,
                'useConfig');
            _setDefaultViewConfig(viewName);
        }
        const value = config?.[key];
        if (typeof (value) === 'undefined') {
            clientLog('warn',
                `no config value found for view: ${viewName} key: ${key}, returning default value.`,
                'useConfig');
            const defaultValue = localStorageConfig[viewName]?.[key];
            if (typeof (defaultValue) === 'undefined') {
                throw new Error('invalid config key: ' + key + ' for view: ' + viewName);
            }
            saveConfig(viewName, key, localStorageConfig[viewName][key]);
            return _parseValue(viewName, key, defaultValue);
        } else {
            return _parseValue(viewName, key, value);
        }
    };

    /**
     * Saves a specific configuration value for a given view and key.
     *
     * @param {string} viewName - The name of the view.
     * @param {string} key - The key of the configuration value to save.
     * @param {*} value - The value to save.
     * @throws {Error} If the view name is invalid.
     */
    const saveConfig = (viewName, key, value) => {
        if (!validViewConfigKeys.includes(viewName)) {
            throw new Error('invalid view key for config: ' + viewName);
        }
        const config = _loadViewConfig(viewName);
        const newConfig = {...config, [key]: value};

        try {
            const savedConfig = JSON.parse(localStorage.getItem('dams')) || {};
            localStorage.setItem('dams', JSON.stringify({...savedConfig, [viewName]: newConfig}));
            clientLog('debug',
                () => `saveConfig for view: ${viewName} key: ${key} value: ${JSON.stringify(value)}`,
                'useConfig');
        } catch (e) {
            clientLog('error',
                `error saving config for view: ${viewName} key: ${key} value: ${String(value)} ${e}`,
                'useConfig');
        }
    };

    /**
     * Clears the selected museum from the 'mainSearch' view configuration.
     */
    const clearMuseum = () => {
        const config = _loadViewConfig('mainSearch');
        const newConfig = {...config, selectedMuseum: null};
        try {
            const savedConfig = JSON.parse(localStorage.getItem('dams')) || {};
            localStorage.setItem('dams', JSON.stringify({...savedConfig, ['mainSearch']: newConfig}));
        } catch (e) {
            clientLog('error',
                `error clearing selected museum from config for view: mainSearch ${e}`,
                'useConfig');
        }
    };

    // Return the public API of the hook
    return {
        getConfig,
        saveConfig,
        clearMuseum
    };
};
